import { Toast } from 'Web/Services/ToastService';
import { ObjectUtils } from '../../../../Helpers/ObjectUtils';
import { ILaizeOpening, ILaizeWall } from '../../Components/Laizes/LaizeCalc';
import { RoomWallPointMove } from '../../Context/MapContext';
import { MapConstants } from '../../MapConstants';
import { IOpening } from '../../Models/IOpening';
import { IRoom, RoomShape, RoomType } from '../../Models/IRoom';
import { IRoomItem } from '../../Models/IRoomItem';
import { Editor } from '../Editor';
import { toOnlyMapRoom } from '../OnlyMapItem';
import { PointUtils } from '../PointUtils';
import { qSVG } from '../qSVG';
import { RoomUtils } from '../Room/RoomUtils';
import { RoomItemUtils } from '../RoomItem/RoomItemUtils';
import { AdjacentRooms, BestVertice, CoordPoint, LockedWall, Wall, WallLine, WallSelected } from '../Types';

export enum WallResizeDirection {
    Both = 'Both',
    FixedStart = 'FixedStart',
    FixedEnd = 'FixedEnd',
    FixedStartCompass = 'FixedStartCompass',
    FixedEndCompass = 'FixedEndCompass',
}

export class WallUtils {
    public static wallSize = (wall: WallLine) => {
        return (qSVG.measure(wall.start, wall.end) * 100) / MapConstants.meter;
    };

    public static createWall(pointA: CoordPoint, pointB: CoordPoint, sideName: string, roomId: string) {
        const wall = Editor.wall(
            pointA,
            pointB,
            'normal',
            MapConstants.wallMinSize(RoomType.Sol), //TODO CHANGE
            RoomShape.Custom, //room.shape,
            roomId,
            sideName
        );
        return wall;
    }
    public static refreshWallsMinimumSizeAndOriginalAngle(walls: Array<Wall>, currentOpenings: Array<IOpening>) {
        for (const wall of walls) {
            let totalObjSizes = 0;
            for (const obj of Editor.objFromWallRoom(wall, currentOpenings)) {
                totalObjSizes += obj.size!;
            }
            wall.minimumSize = totalObjSizes;
            wall.originalAngle = wall.angleRad;
        }
    }

    public static getWallsAdjacentList = (
        walls: (Wall | WallLine)[],
        allWalls: (Wall | WallLine)[],
        roomItems: Array<IRoomItem>
    ): Array<Array<Wall | WallLine>> => {
        //returns only list of wall
        let adjacentWalls = [];
        for (const wallA of walls) {
            for (const wallB of allWalls) {
                if (wallA !== wallB && wallA.roomId !== wallB.roomId && this.testIfWallAdjacent(wallA, wallB)) {
                    adjacentWalls.push([wallA, wallB]);
                }
            }
        }
        return adjacentWalls;
    };

    public static testIfWallAdjacent(wallA: Wall | WallLine, wallB: Wall | WallLine) {
        const tolerance_round_value = MapConstants.tolerance_round_value;
        if (
            wallA.angleRad === wallB.angleRad ||
            Math.abs(Math.abs(wallA.angleRad) - Math.PI) === Math.abs(wallB.angleRad)
        ) {
            if (
                (qSVG.btwn(wallB.start.x, wallA.start.x, wallA.end.x, false, tolerance_round_value) &&
                    qSVG.btwn(wallB.start.y, wallA.start.y, wallA.end.y, false, tolerance_round_value)) ||
                (qSVG.btwn(wallB.end.x, wallA.start.x, wallA.end.x, false, tolerance_round_value) &&
                    qSVG.btwn(wallB.end.y, wallA.start.y, wallA.end.y, false, tolerance_round_value)) ||
                (qSVG.btwn(wallA.start.x, wallB.start.x, wallB.end.x, false, tolerance_round_value) &&
                    qSVG.btwn(wallA.start.y, wallB.start.y, wallB.end.y, false, tolerance_round_value)) ||
                (qSVG.btwn(wallA.end.x, wallB.start.x, wallB.end.x, false, tolerance_round_value) &&
                    qSVG.btwn(wallA.end.y, wallB.start.y, wallB.end.y, false, tolerance_round_value))
            ) {
                //* To avoid #22870
                const points = [wallA.start, wallA.end, wallB.start, wallB.end];
                const supperposedPoint = PointUtils.getSupperposedPoints(points);
                if (supperposedPoint != null) {
                    const bbox = PointUtils.calculateBoundingBox({ points });
                    if (
                        bbox &&
                        (qSVG.strictlyBetween(supperposedPoint.x, bbox.xMin, bbox.xMax) ||
                            qSVG.strictlyBetween(supperposedPoint.y, bbox.yMin, bbox.yMax))
                    ) {
                        return false;
                    }
                }
                return true;
            }
        }
        return false;
    }

    public static calculatePerimeter(walls: Array<Wall>) {
        let value = 0;
        for (const wall of walls) {
            value += qSVG.measure(wall.start, wall.end);
        }
        return value;
    }

    public static determineAdjacentRooms(
        walls: Array<ILaizeWall> = [],
        openings: Array<ILaizeOpening>,
        roomIds: Array<string>
    ): Array<AdjacentRooms> {
        let adjacentRooms: Array<AdjacentRooms> = [];
        const wallsObjs = walls.map((wall) => Editor.objFromWall(wall, openings));
        for (const opening of openings) {
            let appearance: Array<number> = [];
            for (const wallObjs of wallsObjs) {
                //! comment ça peut marcher
                //if (wallObjs.find((x) => x.openingId === opening.openingId)) {
                if (wallObjs.find((x) => x.openingId === opening.openingId)) {
                    appearance.push(wallsObjs.indexOf(wallObjs));
                }
            }
            if (appearance.length > 1) {
                adjacentRooms.push({
                    opening: opening,
                    roomIds: appearance.map((x) => walls[x].roomId!).filter((x) => roomIds.includes(x)),
                    openingAngle: opening.angle!,
                });
            }
        }
        return adjacentRooms;
    }

    public static isWallHorV(wall: WallLine) {
        return this.wallDirection(wall) === 'h' || this.wallDirection(wall) === 'v';
    }

    public static wallDirection(wall: WallLine) {
        const wallAngle = qSVG.angleRadToDeg(wall.angleRad!);
        return this.angleDirection(wallAngle);
    }

    //* when door mode placement activated
    public static nearWallOpening = (
        walls: Array<Wall>,
        rooms: IRoom[],
        snap: CoordPoint,
        range: number = Infinity,
        fromRoomId?: string
    ) => {
        let wallsToWorkWith = fromRoomId ? walls.filter((x) => x.roomId === fromRoomId) : walls;

        if (wallsToWorkWith.length === 0) {
            return undefined;
        }
        if (!fromRoomId) {
            //filter walls if snap is inside a room
            const snapedRoom = Editor.rayCastingRoom(snap, rooms);
            if (snapedRoom) {
                wallsToWorkWith = wallsToWorkWith.filter((x) => x.roomId === snapedRoom.roomId);
            }
        }

        return this.nearWallFromOpening(rooms, snap, range, wallsToWorkWith);
    };

    public static nearWallFromOpening = (
        rooms: IRoom[],
        snap: CoordPoint,
        range = Infinity,
        wallsToWorkWith: Array<Wall>
    ): WallSelected | undefined => {
        let wallDistance = Infinity;
        let wallSelected: WallSelected | undefined = undefined;
        let result;

        for (let e = 0; e < wallsToWorkWith.length; e++) {
            const eq = qSVG.createEquation(
                wallsToWorkWith[e].start.x,
                wallsToWorkWith[e].start.y,
                wallsToWorkWith[e].end.x,
                wallsToWorkWith[e].end.y
            );
            result = qSVG.nearPointOnEquation(eq, snap);
            if (
                result.distance < wallDistance &&
                qSVG.btwn(result.x, wallsToWorkWith[e].start.x, wallsToWorkWith[e].end.x) &&
                qSVG.btwn(result.y, wallsToWorkWith[e].start.y, wallsToWorkWith[e].end.y)
            ) {
                wallDistance = result.distance;
                wallSelected = {
                    wall: wallsToWorkWith[e],
                    roomId: wallsToWorkWith[e].roomId,
                    x: result.x,
                    y: result.y,
                    distance: result.distance,
                    start: wallsToWorkWith[e].start,
                    end: wallsToWorkWith[e].end,
                };
            }
        }

        if (wallDistance <= range) {
            return wallSelected;
        } else {
            return undefined;
        }
    };

    //* when click after door mode placement activated
    public static nearWall = (walls: Wall[], snap: CoordPoint, range: number = Infinity, fromRoomId?: string) => {
        const wallsToWorkWith = fromRoomId ? walls.filter((x) => x.roomId === fromRoomId) : walls;
        return this.nearWallFrom(snap, range, wallsToWorkWith);
    };

    /**
     * Finds the nearest wall.
     * Param prevWall can be used to prioritize this wall when deciding which wall is closer.
     */
    public static nearWallFrom = (
        snap: CoordPoint,
        range: number = Infinity,
        walls: Array<WallLine>,
        prevWall?: WallLine,
        excludeDiagWalls?: boolean
    ): WallSelected | undefined => {
        let wallsToWorkWith = walls;
        let wallDistance = Infinity;
        let wallSelected = undefined;
        let prevWallDistance = Infinity;
        let prevWallSelected = undefined;
        let result;
        let foundWall = false;
        if (wallsToWorkWith.length === 0) {
            return undefined;
        }

        if (excludeDiagWalls) {
            wallsToWorkWith = wallsToWorkWith.filter((x) => this.isWallHorV(x));
        }
        for (let e = 0; e < wallsToWorkWith.length; e++) {
            const eq = qSVG.createEquation(
                wallsToWorkWith[e].start.x,
                wallsToWorkWith[e].start.y,
                wallsToWorkWith[e].end.x,
                wallsToWorkWith[e].end.y
            );
            result = qSVG.nearPointOnEquation(eq, snap);
            if (
                result.distance < wallDistance &&
                qSVG.btwn(result.x, wallsToWorkWith[e].start.x, wallsToWorkWith[e].end.x) &&
                qSVG.btwn(result.y, wallsToWorkWith[e].start.y, wallsToWorkWith[e].end.y)
            ) {
                foundWall = true;
                wallDistance = result.distance;
                wallSelected = {
                    wall: wallsToWorkWith[e],
                    roomId: wallsToWorkWith[e].roomId,
                    sideName: wallsToWorkWith[e].sideName,
                    x: result.x,
                    y: result.y,
                    distance: result.distance,
                    start: wallsToWorkWith[e].start,
                    end: wallsToWorkWith[e].end,
                };
            } else if (prevWall && wallsToWorkWith[e] === prevWall) {
                prevWallDistance = result.distance;
                prevWallSelected = {
                    wall: wallsToWorkWith[e],
                    roomId: wallsToWorkWith[e].roomId,
                    sideName: wallsToWorkWith[e].sideName,
                    x: result.x,
                    y: result.y,
                    distance: result.distance,
                    start: wallsToWorkWith[e].start,
                    end: wallsToWorkWith[e].end,
                };
            }
        }

        if (prevWall && (!foundWall || prevWallDistance + 100 < wallDistance)) {
            wallDistance = prevWallDistance;
            wallSelected = prevWallSelected;
        }
        const vv = this.nearVertice(snap, 10000, wallsToWorkWith);

        if (vv && ((prevWall && vv.distance + 50 < wallDistance) || (!prevWall && vv.distance < wallDistance))) {
            wallDistance = vv.distance;
            wallSelected = {
                wall: vv.number,
                roomId: vv.roomId,
                sideName: vv.number.sideName,
                x: vv.x,
                y: vv.y,
                distance: vv.distance,
                angle: vv.number.angleRad,
                start: vv.number.start,
                end: vv.number.end,
            };
        }
        return wallDistance <= range ? wallSelected : undefined;
    };

    public static nearVertice = (
        snap: CoordPoint,
        range: number = 10000,
        walls: Array<WallLine>
    ): BestVertice | undefined => {
        let bestDistance = Infinity;
        let bestVertice: BestVertice | undefined;
        for (let i = 0; i < walls.length; i++) {
            const wall = walls[i];

            const distance1 = qSVG.gap(snap, { x: wall.start.x, y: wall.start.y });
            const distance2 = qSVG.gap(snap, { x: wall.end.x, y: wall.end.y });

            if (distance1 < distance2 && distance1 < bestDistance) {
                bestDistance = distance1;
                bestVertice = {
                    number: wall,
                    x: wall.start.x,
                    y: wall.start.y,
                    roomId: wall.roomId!,
                    distance: Math.sqrt(bestDistance),
                };
            }
            if (distance2 < distance1 && distance2 < bestDistance) {
                bestDistance = distance2;
                bestVertice = {
                    number: wall,
                    x: wall.end.x,
                    y: wall.end.y,
                    roomId: wall.roomId!,
                    distance: Math.sqrt(bestDistance),
                };
            }
        }
        return bestDistance < range * range ? bestVertice : undefined;
    };

    public static angleDirection(angle: number) {
        if (angle === 0 || angle === 180) {
            return 'h';
        } else if (angle === 90 || angle === -90) {
            return 'v';
        } else if (angle === 45 || angle === -45 || angle === 135 || angle === -135) {
            return 'd';
        }
    }

    public static checkIfWallCanBeMerged = (walls: Array<Wall>, targetWall: Wall): Array<Wall> => {
        const wallsToMerge = walls.filter(
            (x) =>
                x.roomId === targetWall.roomId &&
                x.equations.base.A === targetWall.equations.base.A &&
                x.equations.base.B === targetWall.equations.base.B
        );

        return wallsToMerge;
    };

    public static deleteWall = (roomWalls: Array<Wall>, wallToDelete: Wall): number => {
        if (roomWalls.length > 4) {
            const wallToDeleteIndex = roomWalls.findIndex((x) => x.sideName === wallToDelete.sideName);

            const wallBeforeDeleted = ObjectUtils.atCircular(roomWalls, wallToDeleteIndex - 1);
            const wallAfterDeleted = ObjectUtils.atCircular(roomWalls, wallToDeleteIndex + 2);

            // remove the wall to delete and the next wall
            roomWalls.splice(wallToDeleteIndex, 1);
            roomWalls.splice(wallToDeleteIndex, 1);

            wallBeforeDeleted!.end = ObjectUtils.clone(wallAfterDeleted!.start);
            wallAfterDeleted!.start = ObjectUtils.clone(wallBeforeDeleted!.end);
            return 0;
        }
        return -1;
    };

    public static deleteSingleWall = (roomWalls: Array<Wall>, wallToDelete: Wall): number => {
        if (roomWalls.length > 3) {
            const wallToDeleteIndex = roomWalls.findIndex((x) => x.sideName === wallToDelete.sideName);

            const wallBeforeDeleted = ObjectUtils.atCircular(roomWalls, wallToDeleteIndex - 1);
            const wallAfterDeleted = ObjectUtils.atCircular(roomWalls, wallToDeleteIndex + 1);

            roomWalls.splice(wallToDeleteIndex, 1);

            wallBeforeDeleted!.end = ObjectUtils.clone(wallAfterDeleted!.start);
            wallAfterDeleted!.start = ObjectUtils.clone(wallBeforeDeleted!.end);
            return 0;
        }
        return -1;
    };

    /**
     * @returns the number of walls merged
     */
    public static mergeAlignedWalls = (roomWalls: Array<Wall>): number => {
        let mergeCount = 0;
        if (roomWalls.length > 3) {
            let i = 1;
            while (i < roomWalls.length) {
                const currentWall = ObjectUtils.atCircular(roomWalls, i);
                const previousWall = ObjectUtils.atCircular(roomWalls, i - 1);
                const nextWall = ObjectUtils.atCircular(roomWalls, i + 1);
                if (
                    currentWall?.equations.base.A === previousWall?.equations.base.A &&
                    currentWall?.equations.base.B === previousWall?.equations.base.B
                ) {
                    roomWalls.splice(i, 1);
                    previousWall!.end = ObjectUtils.clone(nextWall!.start);
                    mergeCount++;
                } else {
                    i++;
                }
            }
        }
        return mergeCount;
    };

    public static getSize0Walls = (roomWalls: Array<Wall>) => {
        return roomWalls.filter((x) => qSVG.measure(x.start, x.end) < 0.1);
    }

    public static deleteSize0Walls = (roomWalls: Array<Wall>) => {
        const roomWallsToDelete = WallUtils.getSize0Walls(roomWalls);
        for (const wallToDelete of roomWallsToDelete) {
            WallUtils.deleteSingleWall(roomWalls, wallToDelete);
        }
        return roomWallsToDelete.length;
    };

    public static splitWall = (allWalls: Wall[], allRooms: IRoom[], wallToSplit: Wall, cursorSnap: CoordPoint) => {
        const targetRoomId = wallToSplit.roomId;
        const splitPoint = qSVG.nearPointOnEquation(wallToSplit.equations.base, cursorSnap);
        const otherRoomsWalls = allWalls.filter((x) => x.roomId !== targetRoomId);
        const targetRoomWalls = allWalls.filter((x) => x.roomId === targetRoomId);

        const newTargetRoomPoints = [];
        for (const wall of targetRoomWalls) {
            newTargetRoomPoints.push(wall.start);
            if (wall.sideName === wallToSplit.sideName) {
                newTargetRoomPoints.push(splitPoint);
                newTargetRoomPoints.push(splitPoint);
            }
        }

        return WallUtils.rebuildWalls(newTargetRoomPoints, allRooms, otherRoomsWalls, targetRoomId!);
    };

    public static resizeWall = (
        allWalls: Wall[],
        allRooms: IRoom[],
        wallToResize: Wall,
        size: number,
        direction: WallResizeDirection
    ) => {
        const targetRoomId = wallToResize.roomId;
        let newStart;
        let newEnd;
        switch (direction) {
            case WallResizeDirection.FixedStart:
                newStart = wallToResize.start;

                newEnd = {
                    x: wallToResize.start.x + Math.cos(wallToResize.angleRad) * size,
                    y: wallToResize.start.y + Math.sin(wallToResize.angleRad) * size,
                };
                break;

            case WallResizeDirection.FixedEnd:
                newStart = {
                    x: wallToResize.end.x - Math.cos(wallToResize.angleRad) * size,
                    y: wallToResize.end.y - Math.sin(wallToResize.angleRad) * size,
                };

                newEnd = wallToResize.end;
                break;

            case WallResizeDirection.FixedStartCompass:
                newStart = wallToResize.start;

                const wallChild = WallUtils.getWallChild(wallToResize, allWalls)!;
                const pointsChild = PointUtils.findPointsOnCircleAtDistance(
                    wallChild.end,
                    WallUtils.wallSize(wallChild),
                    wallToResize.start,
                    size
                );

                if (pointsChild.length > 0) {
                    newEnd = pointsChild[0];
                } else {
                    Toast.error('ERROR_NO_POINTS_FOUND');
                    // console.log('No points found on circle at distance');
                    newEnd = wallToResize.end;
                }
                break;

            case WallResizeDirection.FixedEndCompass:
                newEnd = wallToResize.end;

                const wallParent = WallUtils.getWallParent(wallToResize, allWalls)!;
                const pointsParent = PointUtils.findPointsOnCircleAtDistance(
                    wallParent.start,
                    WallUtils.wallSize(wallParent),
                    wallToResize.end,
                    size
                );

                if (pointsParent.length > 0) {
                    newStart = pointsParent[pointsParent.length - 1];
                } else {
                    Toast.error('ERROR_NO_POINTS_FOUND');
                    // console.log('No points found on circle at distance');
                    newStart = wallToResize.start;
                }
                break;

            case WallResizeDirection.Both:
            default:
                const centerWallToResize = PointUtils.bboxCenter([wallToResize.start, wallToResize.end]);

                newStart = {
                    x: centerWallToResize.x - (Math.cos(wallToResize.angleRad) * size) / 2,
                    y: centerWallToResize.y - (Math.sin(wallToResize.angleRad) * size) / 2,
                };

                newEnd = {
                    x: centerWallToResize.x + (Math.cos(wallToResize.angleRad) * size) / 2,
                    y: centerWallToResize.y + (Math.sin(wallToResize.angleRad) * size) / 2,
                };
                break;
        }

        const otherRoomsWalls = allWalls.filter((x) => x.roomId !== wallToResize.roomId);
        const targetRoomWalls = allWalls.filter((x) => x.roomId === wallToResize.roomId);

        const newTargetRoomPoints = [];
        for (const wall of targetRoomWalls) {
            if (ObjectUtils.deepEqual(wall.start, wallToResize.start)) {
                newTargetRoomPoints.push(newStart);
            } else if (ObjectUtils.deepEqual(wall.start, wallToResize.end)) {
                newTargetRoomPoints.push(newEnd);
            } else {
                newTargetRoomPoints.push(wall.start);
            }
        }

        return WallUtils.rebuildWalls(newTargetRoomPoints, allRooms, otherRoomsWalls, targetRoomId!);
    };

    public static rebuildWalls = (
        newRoomPoints: CoordPoint[],
        allRooms: IRoom[],
        otherRoomsWalls: Wall[],
        targetRoomId: string
    ): { newAllWalls: Wall[]; newAllRooms: IRoom[] } => {
        var newRoomPointsClosedShape = [...newRoomPoints, newRoomPoints[0]];

        const roomWalls: Array<Wall> = [];
        for (let i = 0; i < newRoomPoints.length; i++) {
            const targetRoom = allRooms.find((x) => x.roomId === targetRoomId)!;
            const newWall = WallUtils.createWall(
                newRoomPoints[i],
                newRoomPoints[(i + 1) % newRoomPoints.length],
                `side_${i}`,
                targetRoom.roomId!
            );
            newWall.angleRad = qSVG.getAngle(newRoomPoints[i], newRoomPoints[(i + 1) % newRoomPoints.length]).rad;
            newWall.originalAngle = newWall.angleRad;
            roomWalls.push(newWall);
        }

        const newAllWalls = [...otherRoomsWalls, ...roomWalls];

        const newAllRooms = allRooms.map((room) => {
            return {
                ...room,
                walls: room.roomId! === targetRoomId ? roomWalls : room.walls,
                coords: room.roomId! === targetRoomId ? newRoomPointsClosedShape : room.coords,
            };
        });

        newAllRooms.forEach((room) => {
            if (room.roomId === targetRoomId) {
                // if the flooring wall is deleted, set the first wall as flooring wall
                if (!room.walls?.find((x) => x.sideName === room.flooringWallStart?.sideName)) {
                    room.flooringWallStart = room.walls![0];
                } else {
                    room.flooringWallStart = room.walls?.find((x) => x.sideName === room.flooringWallStart?.sideName);
                }
            }
        });

        return { newAllWalls: newAllWalls, newAllRooms: newAllRooms };
    };

    public static updateRoomWallsToFollowPoint = (roomWallPointMove: RoomWallPointMove, snap: CoordPoint): number => {
        if (roomWallPointMove.wallStartToMove && roomWallPointMove.wallEndToMove) {
            roomWallPointMove.wallStartToMove.start = ObjectUtils.clone(snap);
            roomWallPointMove.wallStartToMove.angleRad = qSVG.getAngle(
                roomWallPointMove.wallStartToMove.start,
                roomWallPointMove.wallStartToMove.end
            ).rad;
            roomWallPointMove.wallEndToMove.end = ObjectUtils.clone(snap);
            roomWallPointMove.wallEndToMove.angleRad = qSVG.getAngle(
                roomWallPointMove.wallEndToMove.start,
                roomWallPointMove.wallEndToMove.end
            ).rad;
            return 0;
        }
        return -1;
        // const roomWallsUpdated: CircularLinkedList<Wall> = roomWalls!. map((wall, index) => {
        //     const wallStart = index === roomWallPointMove.indexWallStartToMove ? { x: snap.x, y: snap.y } : wall.start;
        //     const wallEnd = index === roomWallPointMove.indexWallEndToMove ? { x: snap.x, y: snap.y } : wall.end;
        //     return {
        //         ...wall,
        //         start: wallStart,
        //         end: wallEnd,
        //         angle: qSVG.getAngle(wallStart, wallEnd).rad,
        //         equations: {
        //             ...wall.equations,
        //             base: qSVG.createEquation(wallStart.x, wallStart.y, wallEnd.x, wallEnd.y),
        //         },
        //     };
        // });
        // return roomWallsUpdated;
    };

    public static getBestWallResizeDirections = (
        targetWall: Wall,
        lockedWallsList: Array<LockedWall>,
        roomWalls: Array<Wall>,
        otherWalls: Array<Wall>
    ): Array<{ direction: WallResizeDirection; limit: { min: number; max: number } }> => {
        const notLockedWallDirections: Array<{ direction: WallResizeDirection; limit: { min: number; max: number } }> =
            [];
        const targetWallIndex = roomWalls.findIndex((x) => x.wallId === targetWall.wallId);
        const targetWallOgChildIndex = ObjectUtils.findIndexCircular(roomWalls, targetWallIndex + 1);
        const targetWallOgParentIndex = ObjectUtils.findIndexCircular(roomWalls, targetWallIndex - 1);
        const hasParentLocked = lockedWallsList.some(
            (x) => x.wallIndex === targetWallOgParentIndex && x.roomId === targetWall?.roomId
        );
        const hasChildLocked = lockedWallsList.some(
            (x) => x.wallIndex === targetWallOgChildIndex && x.roomId === targetWall?.roomId
        );
        const hasItselfLocked = lockedWallsList.some(
            (x) => x.wallIndex === targetWallIndex && x.roomId === targetWall?.roomId
        );

        if (!hasParentLocked && !hasChildLocked) {
            notLockedWallDirections.push({
                direction: WallResizeDirection.Both,
                limit: { min: 20, max: MapConstants.wallMaxSize },
            });
        }
        if (!hasChildLocked) {
            notLockedWallDirections.push({
                direction: WallResizeDirection.FixedStart,
                limit: { min: 20, max: MapConstants.wallMaxSize },
            });
        }
        if (!hasParentLocked) {
            notLockedWallDirections.push({
                direction: WallResizeDirection.FixedEnd,
                limit: { min: 20, max: MapConstants.wallMaxSize },
            });
        }

        // If both parent and child are locked, we can still resize the wall if it is not locked itself
        if (notLockedWallDirections.length === 0 && !hasItselfLocked) {
            // const bestDirection = notLockedWallDirections[0];
            const otherWallsPoints = otherWalls.map((x) => x.start);

            // check the values if we move point from the parent side
            const wallParent = WallUtils.getWallParent(targetWall, roomWalls);
            const findClosestPointOnParentSide = PointUtils.findClosestPointOnCircle(
                wallParent!.start,
                WallUtils.wallSize(wallParent!),
                targetWall.end
            );
            const findFarthestPointOnCircle = PointUtils.findFarthestPointOnCircle(
                wallParent!.start,
                WallUtils.wallSize(wallParent!),
                targetWall.end
            );

            const findCircleWallIntersectionsParent = WallUtils.findCircleWallIntersections(
                wallParent!.start,
                WallUtils.wallSize(wallParent!),
                otherWalls
            );

            const otherWallsPointsWithinCircleParent = PointUtils.filterPointsInsideCircle(
                wallParent!.start,
                WallUtils.wallSize(wallParent!),
                otherWallsPoints,
                true
            );

            const parentCircleWallIntersectionPassingByOtherWallPoints = otherWallsPointsWithinCircleParent
                .map((point) => {
                    return PointUtils.findCircleIntersectionFromCenter(
                        wallParent!.start,
                        WallUtils.wallSize(wallParent!),
                        point
                    );
                })
                .filter((x) => x !== null) as CoordPoint[];

            const parentSideMinSize = Math.ceil(qSVG.measure(findClosestPointOnParentSide, targetWall.end));
            const parentSideMaxSize = Math.floor(
                Math.min(
                    ...[
                        ...findCircleWallIntersectionsParent.map((x) => qSVG.measure(x.point, targetWall.end)),
                        ...parentCircleWallIntersectionPassingByOtherWallPoints.map((x) =>
                            qSVG.measure(x, targetWall.end)
                        ),
                        qSVG.measure(findFarthestPointOnCircle, targetWall.end),
                    ]
                )
            );

            // check the values if we move point from the child side
            const wallChild = WallUtils.getWallChild(targetWall, roomWalls);
            const findClosestPointOnChildSide = PointUtils.findClosestPointOnCircle(
                wallChild!.end,
                WallUtils.wallSize(wallChild!),
                targetWall.start
            );
            const findFarthestPointOnCircleChild = PointUtils.findFarthestPointOnCircle(
                wallChild!.end,
                WallUtils.wallSize(wallChild!),
                targetWall.start
            );

            const findCircleWallIntersectionsChild = WallUtils.findCircleWallIntersections(
                wallChild!.end,
                WallUtils.wallSize(wallChild!),
                otherWalls
            );

            const otherWallsPointsWithinCircleChild = PointUtils.filterPointsInsideCircle(
                wallChild!.end,
                WallUtils.wallSize(wallChild!),
                otherWallsPoints,
                true
            );

            const childCircleWallIntersectionPassingByOtherWallPoints = otherWallsPointsWithinCircleChild
                .map((point) => {
                    return PointUtils.findCircleIntersectionFromCenter(
                        wallChild!.end,
                        WallUtils.wallSize(wallChild!),
                        point
                    );
                })
                .filter((x) => x !== null) as CoordPoint[];

            const childSideMinSize = Math.ceil(qSVG.measure(findClosestPointOnChildSide, targetWall.start));
            const childSideMaxSize = Math.floor(
                Math.min(
                    ...[
                        ...findCircleWallIntersectionsChild.map((x) => qSVG.measure(x.point, targetWall.start)),
                        ...childCircleWallIntersectionPassingByOtherWallPoints.map((x) =>
                            qSVG.measure(x, targetWall.start)
                        ),
                        qSVG.measure(findFarthestPointOnCircleChild, targetWall.start),
                    ]
                )
            );

            // console.log({
            //     parentSideMinSize,
            //     parentSideMaxSize,
            //     childSideMinSize,
            //     childSideMaxSize,
            // });

            // console.log({
            //     'WallUtils.wallSize(targetWall)': WallUtils.wallSize(targetWall),
            //     childSideMaxSize,
            //     parentSideMaxSize,
            // });
            if (childSideMaxSize > parentSideMaxSize) {
                if (parentSideMaxSize - WallUtils.wallSize(targetWall) < 1) {
                    notLockedWallDirections.push({
                        direction: WallResizeDirection.FixedEndCompass,
                        limit: { min: parentSideMinSize, max: parentSideMaxSize },
                    });
                } else {
                    notLockedWallDirections.push({
                        direction: WallResizeDirection.FixedStartCompass,
                        limit: { min: childSideMinSize, max: childSideMaxSize },
                    });
                }
            } else {
                if (childSideMaxSize - WallUtils.wallSize(targetWall) < 1) {
                    notLockedWallDirections.push({
                        direction: WallResizeDirection.FixedEndCompass,
                        limit: { min: parentSideMinSize, max: parentSideMaxSize },
                    });
                } else {
                    notLockedWallDirections.push({
                        direction: WallResizeDirection.FixedStartCompass,
                        limit: { min: childSideMinSize, max: childSideMaxSize },
                    });
                }
            }
        }
        return notLockedWallDirections;
    };

    public static getWallParent = (wall: Wall, siblingWalls: Wall[]) => {
        const wallIndex = siblingWalls.findIndex((w) => w.wallId === wall.wallId);
        if (wallIndex === -1) {
            return null;
        }
        const wallParent = siblingWalls.at(wallIndex - 1)!;
        return wallParent;
    };

    public static getWallChild = (wall: Wall, siblingWalls: Wall[]) => {
        const wallIndex = siblingWalls.findIndex((w) => w.wallId === wall.wallId);
        if (wallIndex === -1) {
            return null;
        }
        const wallChild = siblingWalls.at((wallIndex + 1) % siblingWalls.length)!;
        return wallChild;
    };

    public static checkWallDeletetionValidity = (room: IRoom, targetWall: Wall) => {
        const mapRoom = ObjectUtils.clone(toOnlyMapRoom(room));
        const invalidRoomItemIds: string[] = [];
        const wallTargetChildId = WallUtils.getWallChild(targetWall, mapRoom.walls)?.wallId;
        mapRoom.walls.splice(
            mapRoom.walls.findIndex((w) => w.wallId === targetWall.wallId),
            1
        );
        mapRoom.walls.splice(
            mapRoom.walls.findIndex((w) => w.wallId === wallTargetChildId),
            1
        );

        mapRoom.coords = RoomUtils.getCoordsFromWalls(mapRoom as IRoom);
        for (const roomItem of mapRoom.roomItems) {
            const isInvalid = RoomItemUtils.isRoomItemInsideRoomOrInContactWithRoom(roomItem, mapRoom, false);
            if (isInvalid) {
                invalidRoomItemIds.push(roomItem.roomItemId!);
            }
        }
        return { invalidRoomItemIds };
    };

    /**
     * Finds all intersection points between a circle and an array of walls.
     *
     * @param coordinateA Center of the circle
     * @param radiusA Radius of the circle
     * @param allWalls Array of walls to check for intersections
     * @returns Array of intersection points with wall information
     */
    public static findCircleWallIntersections(
        coordinateA: CoordPoint,
        radiusA: number,
        allWalls: Wall[]
    ): Array<{ point: CoordPoint; wall: Wall; wallIndex: number }> {
        const intersections: Array<{ point: CoordPoint; wall: Wall; wallIndex: number }> = [];

        for (let wallIndex = 0; wallIndex < allWalls.length; wallIndex++) {
            const wall = allWalls[wallIndex];
            const wallIntersections = this.findCircleLineSegmentIntersections(
                coordinateA,
                radiusA,
                wall.start,
                wall.end
            );

            // Add wall information to each intersection point
            wallIntersections.forEach((point) => {
                intersections.push({
                    point,
                    wall,
                    wallIndex,
                });
            });
        }

        return intersections;
    }

    /**
     * Finds intersection points between a circle and a line segment (wall).
     *
     * @param circleCenter Center of the circle
     * @param radius Radius of the circle
     * @param lineStart Start point of the line segment
     * @param lineEnd End point of the line segment
     * @returns Array of intersection points (0, 1, or 2 points)
     */
    private static findCircleLineSegmentIntersections(
        circleCenter: CoordPoint,
        radius: number,
        lineStart: CoordPoint,
        lineEnd: CoordPoint
    ): CoordPoint[] {
        const intersections: CoordPoint[] = [];

        // Vector from line start to line end
        const dx = lineEnd.x - lineStart.x;
        const dy = lineEnd.y - lineStart.y;

        // Vector from line start to circle center
        const fx = lineStart.x - circleCenter.x;
        const fy = lineStart.y - circleCenter.y;

        // Quadratic equation coefficients: at² + bt + c = 0
        const a = dx * dx + dy * dy;
        const b = 2 * (fx * dx + fy * dy);
        const c = fx * fx + fy * fy - radius * radius;

        // Check if line has zero length
        if (a === 0) {
            // Line is a point, check if it's on the circle
            const distanceToCenter = Math.sqrt(fx * fx + fy * fy);
            if (Math.abs(distanceToCenter - radius) < 1e-10) {
                intersections.push({ ...lineStart });
            }
            return intersections;
        }

        const discriminant = b * b - 4 * a * c;

        // No intersection if discriminant is negative
        if (discriminant < 0) {
            return intersections;
        }

        // Calculate intersection parameters
        const sqrt_discriminant = Math.sqrt(discriminant);
        const t1 = (-b - sqrt_discriminant) / (2 * a);
        const t2 = (-b + sqrt_discriminant) / (2 * a);

        // Check if intersections are within the line segment (t between 0 and 1)
        if (t1 >= 0 && t1 <= 1) {
            intersections.push({
                x: lineStart.x + t1 * dx,
                y: lineStart.y + t1 * dy,
            });
        }

        // Only add second intersection if it's different from the first (tangent case)
        if (discriminant > 1e-10 && t2 >= 0 && t2 <= 1) {
            intersections.push({
                x: lineStart.x + t2 * dx,
                y: lineStart.y + t2 * dy,
            });
        }

        return intersections;
    }
}
